import { Skeleton } from 'antd';
import React from 'react';

interface Props {
  isLoading?: boolean;
  children?: JSX.Element | string;
}

export const EmailSectionSkeleton: React.FC<Props> = (props: Props) => {
  const { children, isLoading } = props;

  const attrs = {
    wrapper: { className: 'email' },
    header: {
      wrapper: { className: 'email-header' },
      leftSectionWrapper: { className: 'email-header__left-section' },
      from: { className: 'email-header__left-section__from' },
      centralSection: { className: 'email-header__left-section__central' },
      date: { className: 'email-header-date' },
    },
    body: {
      wrapper: { className: 'email__body' },
      firstColumn: {
        wrapper: { className: 'email__body__first-column' },
        emailMessage: {
          style: {
            height: '265px',
            padding: '22px',
            backgroundColor: '#fafaff',
          },
        },
        topics: {
          wrapper: { className: 'topics' },
          header: { className: 'topics__header' },
          body: {
            wrapper: {
              className: 'topics__body',
            },
          },
        },
      },
      secondColumn: {
        wrapper: {
          className: 'email__body__second-column',
          style: { border: 'none', backgroundColor: '#fafaff' },
        },
        upperHand: {
          wrapper: { className: 'upper-hand' },
          header: { className: 'upper-hand__header' },
          body: {
            wrapper: { className: 'upper-hand__body' },
            leftPerson: { className: 'upper-hand__body__left-person' },
            rightPerson: { className: 'upper-hand__body__right-person' },
          },
        },
        replyTime: {
          wrapper: { className: 'reply-time' },
          header: { className: 'reply-time__header' },
          body: {
            wrapper: { className: 'reply-time__body' },
            leftPerson: { className: 'reply-time__body__left-person' },
            divider: {
              className: 'reply-time__body-divider',
              style: { opacity: 0 },
            },
            rightPerson: { className: 'reply-time__body__right-person' },
          },
          footer: { className: 'reply-time__footer' },
        },
        assesment: {
          wrapper: { className: 'assessment' },
          header: { className: 'assessment__header' },
          body: {
            wrapper: { className: 'assessment__body' },
            messagesRow: { className: 'assessment__body-message' },
          },
        },
      },
      thirdColumn: {
        wrapper: {
          className: 'email__body__third-column',
          style: { border: 'none', backgroundColor: '#fafaff' },
        },
        sentiment: {
          wrapper: { className: 'your-sentiment' },
          header: { className: 'your-sentiment__header' },
          body: {
            className: 'your-sentiment__body',
            style: { height: '80px', marginBottom: '30px', marginTop: '31px' },
          },
        },
        wordCount: {
          wrapper: { className: 'word-count' },
          header: { className: 'word-count__header' },
          body: {
            wrapper: { className: 'word-count__body' },
            leftPerson: { className: 'word-count__body__left-person' },
            rightPerson: { className: 'word-count__body__right-person' },
          },
          footer: { className: 'word-count__footer' },
        },
        nextMove: {
          wrapper: {
            className: 'reply-time',
            style: {
              border: 'none',
            },
          },
          header: { className: 'reply-time__header' },
          body: {
            style: {
              height: '135px',
              padding: '10px',
              display: 'flex',
            },
          },
        },
      },
    },
    footer: { className: 'email-footer' },
    skeletons: {
      header: {
        loading: isLoading,
        active: true,
        avatar: false,
        paragraph: false,
        title: {
          style: {
            width: 82,
            maxWidth: '100%',
            height: 13,
            borderRadius: 2,
            marginTop: 0,
          },
        },
      },
      basicTitleSkeleton: {
        loading: isLoading,
        active: true,
        avatar: false,
        paragraph: false,
      },
    },
  };

  const setTitleSkeleton = (
    width: string,
    margin?: string,
    height?: number,
    radius?: string,
    invisible?: boolean
  ) => {
    return (
      <div
        style={
          invisible
            ? { opacity: 0, width: 'max-content' }
            : { width: 'max-content' }
        }
      >
        <Skeleton
          {...attrs.skeletons.basicTitleSkeleton}
          title={{
            style: {
              width,
              height: height?.toString().length ? height : 12,
              borderRadius: radius?.length ? radius : 2,
              margin: margin?.length ? margin : 0,
            },
          }}
        />
      </div>
    );
  };

  const emailSectionHeader = (
    <div {...attrs.header.wrapper}>
      <div>{setTitleSkeleton('40px', '0px 10px 0px 0px', 40)}</div>
      <div {...attrs.header.leftSectionWrapper}>
        <div {...attrs.header.from}>
          <div>{setTitleSkeleton('100px', '0px', 14)}</div>
        </div>
        <div {...attrs.header.centralSection}>
          {setTitleSkeleton('100px', '0px 0px 0px 20px', 14)}
          {setTitleSkeleton('100px', '0px 0px 0px 40px', 14)}
        </div>
      </div>
      <div {...attrs.header.date}>{setTitleSkeleton('100px', '0px', 14)}</div>
    </div>
  );

  const emailMessage = (
    <div {...attrs.body.firstColumn.emailMessage}>
      {setTitleSkeleton('150px', '-2px 0px 0px 0px', 9)}
      {setTitleSkeleton('180px', '15px 0px 0px 0px', 9)}
      {setTitleSkeleton('160px', '8px 0px 0px 0px', 9)}
      {setTitleSkeleton('110px', '8px 0px 0px 0px', 9)}
      {setTitleSkeleton('130px', '8px 0px 0px 0px', 9)}
      {setTitleSkeleton('120px', '8px 0px 0px 0px', 9)}
    </div>
  );

  const topics = (
    <div {...attrs.body.firstColumn.topics.wrapper}>
      <div {...attrs.body.firstColumn.topics.header}>
        {setTitleSkeleton('50px', '-2px 0px 0px 0px', 14)}
      </div>
      <div {...attrs.body.firstColumn.topics.body.wrapper}>
        {setTitleSkeleton('80px', '5px', 22)}
        {setTitleSkeleton('60px', '5px', 22)}
        {setTitleSkeleton('70px', '5px', 22)}
        {setTitleSkeleton('90px', '5px', 22)}
        {setTitleSkeleton('50px', '5px', 22)}
        {setTitleSkeleton('70px', '5px', 22)}
      </div>
    </div>
  );

  const upperHand = (
    <div {...attrs.body.secondColumn.upperHand.wrapper}>
      <div {...attrs.body.secondColumn.upperHand.header}>
        {setTitleSkeleton('80px', '-2px 0px 0px 0px', 15)}
      </div>
      <div {...attrs.body.secondColumn.upperHand.body.wrapper}>
        <div {...attrs.body.secondColumn.upperHand.body.leftPerson}>
          {setTitleSkeleton('70px', '-2px 0px 0px 0px', 10)}
          {setTitleSkeleton('40px', '-2px 10px 0px 8px', 40, '50%', true)}
        </div>
        <div {...attrs.body.secondColumn.upperHand.body.rightPerson}>
          {setTitleSkeleton('60px', '-2px 10px 0px -5px', 60, '50%', true)}
          {setTitleSkeleton('60px', '-2px 0px 0px 0px', 10)}
        </div>
      </div>
    </div>
  );

  const replyTime = (
    <div {...attrs.body.secondColumn.replyTime.wrapper}>
      <div {...attrs.body.secondColumn.replyTime.header}>
        {setTitleSkeleton('120px', '0px 0px 0px 0px', 15)}
      </div>
      <div {...attrs.body.secondColumn.replyTime.body.wrapper}>
        <div {...attrs.body.secondColumn.replyTime.body.leftPerson}>
          {setTitleSkeleton('30px', '1px 0px 0px 0px', 30, '10px', true)}
          {setTitleSkeleton('40px', '6px 0px 0px 0px', 10, '10px', true)}
        </div>
        <div {...attrs.body.secondColumn.replyTime.body.divider} />
        <div {...attrs.body.secondColumn.replyTime.body.rightPerson}>
          {setTitleSkeleton('30px', '1px 0px 0px 0px', 30, '10px', true)}
          {setTitleSkeleton('40px', '6px 0px 0px 0px', 10, '10px', true)}
        </div>
      </div>
      <div {...attrs.body.secondColumn.replyTime.footer}>
        {setTitleSkeleton('150px', '4px 0px 0px 0px', 11)}
      </div>
    </div>
  );

  const assesment = (
    <div {...attrs.body.secondColumn.assesment.wrapper}>
      <div {...attrs.body.secondColumn.assesment.header}>
        {setTitleSkeleton('80px', '0px 0px 0px 0px', 15)}
      </div>
      <div {...attrs.body.secondColumn.assesment.body.wrapper}>
        <div style={{ display: 'flex' }}>
          {setTitleSkeleton('10px', '5px 15px 0px 3px', 10, '50%')}
          {setTitleSkeleton('120px', '5px 0px 0px 0px', 10)}
        </div>
        <div style={{ display: 'flex', marginTop: '5px' }}>
          {setTitleSkeleton('10px', '5px 15px 0px 3px', 10, '50%')}
          {setTitleSkeleton('160px', '5px 0px 0px 0px', 10)}
        </div>
        <div style={{ display: 'flex', marginTop: '5px' }}>
          {setTitleSkeleton('10px', '5px 15px 0px 3px', 10, '50%')}
          {setTitleSkeleton('110px', '5px 0px 0px 0px', 10)}
        </div>
        <div style={{ display: 'flex', marginTop: '5px' }}>
          {setTitleSkeleton('10px', '5px 15px 0px 3px', 10, '50%')}
          {setTitleSkeleton('140px', '5px 0px 0px 0px', 10)}
        </div>
      </div>
    </div>
  );

  const sentiment = (
    <div {...attrs.body.thirdColumn.sentiment.wrapper}>
      <div {...attrs.body.thirdColumn.sentiment.header}>
        {setTitleSkeleton('80px', '0px 0px 0px 0px', 15)}
      </div>
      <div {...attrs.body.thirdColumn.sentiment.body}>
        {setTitleSkeleton('50px', '0px 15px 0px 0px', 10)}
        {setTitleSkeleton('150px', '-2px 0px 0px 0px', 12, '15px')}
        {setTitleSkeleton('50px', '0px 0px 0px 15px', 10)}
      </div>
    </div>
  );

  const wordCount = (
    <div {...attrs.body.thirdColumn.wordCount.wrapper}>
      <div {...attrs.body.thirdColumn.wordCount.header}>
        {setTitleSkeleton('120px', '0px 0px 0px 0px', 15)}
      </div>
      <div {...attrs.body.thirdColumn.wordCount.body.wrapper}>
        <div {...attrs.body.thirdColumn.wordCount.body.leftPerson}>
          {setTitleSkeleton('30px', '1px 0px 0px 0px', 30, '10px', true)}
          {setTitleSkeleton('40px', '6px 0px 0px 0px', 10, '10px', true)}
        </div>
        <div {...attrs.body.thirdColumn.wordCount.body.rightPerson}>
          {setTitleSkeleton('30px', '1px 0px 0px 0px', 30, '10px', true)}
          {setTitleSkeleton('40px', '6px 0px 0px 0px', 10, '10px', true)}
        </div>
      </div>
      <div {...attrs.body.thirdColumn.wordCount.footer}>
        {setTitleSkeleton('150px', '4px 0px 0px 0px', 11)}
      </div>
    </div>
  );

  const nextMove = (
    <div {...attrs.body.thirdColumn.nextMove.wrapper}>
      <div {...attrs.body.thirdColumn.nextMove.header}>
        {setTitleSkeleton('100px', '30px 0px 0px 13px', 15)}
      </div>
      <div {...attrs.body.thirdColumn.nextMove.body}>
        <div style={{ display: 'flex', margin: '11px 0px 0px 4px' }}>
          {setTitleSkeleton('10px', '5px 15px 0px 3px', 10, '50%')}
          {setTitleSkeleton('120px', '5px 0px 0px 0px', 10)}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isLoading ? (
        <div {...attrs.wrapper}>
          {emailSectionHeader}
          <div {...attrs.body.wrapper}>
            <div {...attrs.body.firstColumn.wrapper}>
              {emailMessage} {topics}
            </div>
            <div {...attrs.body.secondColumn.wrapper}>
              {upperHand}
              {replyTime}
              {assesment}
            </div>
            <div {...attrs.body.thirdColumn.wrapper}>
              {sentiment}
              {wordCount}
              {nextMove}
            </div>
          </div>
          <div {...attrs.footer} />
        </div>
      ) : (
        children
      )}
    </>
  );
};
